import { Dispatch, SetStateAction, useState } from 'react'
import { Permissions } from '../../../../types/user-types'
import { canEditWell } from '../../../services/user-utils'
import useUser from '../../../hooks/use-user'
import SummaryTable from '../../wells/summary-table'
import NoAquiferTests from './no-aquifer-tests'
import { ERROR_MESSAGES } from '../../errors/messages'
import { useWellId } from '../../../hooks/use-well-id'
import { useNavigate } from 'react-router-dom'
import { Button, ButtonsContainer, Card, ColumnHeader, HeadingRow, TableAction } from '@msaf/core-react'

interface AquiferTestSummaryTableProps {
  fields: Record<string, string | number | boolean>[] | null
  setFields: Dispatch<SetStateAction<Record<string, string | number | boolean>[] | null>>
  handleNoTestsCheckbox?: (checked: boolean) => void
  isNoTestsChecked?: boolean
  isViewMode?: boolean
}

export function AquiferTestSummaryTable({
  fields,
  setFields,
  handleNoTestsCheckbox,
  isNoTestsChecked,
  isViewMode = false,
}: AquiferTestSummaryTableProps) {
  const navigate = useNavigate()
  const { user } = useUser()
  const wellId = useWellId()

  // Delete confirmation modal state
  const [rowId, setDeletionRowId] = useState<number | null>(null)

  const onModalClose = () => {
    setDeletionRowId(null)
  }

  const rowClickAction: TableAction = {
    label: 'View',
    type: 'transitionAction',
    // FIXME-NWP Routing for existing well records edit
    args: [{ constant: `wells/${wellId}/view/aquifer-tests` }, { elementKey: 'id' }],
  }

  const actions: Array<TableAction> = [
    {
      label: 'Delete',
      type: 'deleteAction',
      args: [
        {
          elementKey: 'id',
        },
      ],
      actionPermissions: [Permissions.DELETE_WELL_DATA],
    },
  ]

  const columnHeaders: Array<ColumnHeader> = [
    {
      elementKey: 'id',
      columnHeading: 'Id',
      viewColumn: 'id',
      type: 'hidden',
    },
    {
      elementKey: 'dateCompleted',
      columnHeading: 'Date completed',
      viewColumn: 'testDate',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'testType',
      columnHeading: 'Test type',
      viewColumn: 'testType',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'analysisMethod',
      columnHeading: 'Analysis method',
      viewColumn: 'analysisMethod',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'transmissivity',
      columnHeading: 'Transmissivity',
      viewColumn: 'transmissivity',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'storativity',
      columnHeading: 'Storativity',
      viewColumn: 'storativity',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'parameterRating',
      columnHeading: 'Parameter Rating',
      viewColumn: 'parameterAccuracy',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'actions',
      columnHeading: 'Actions',
      viewColumn: 'actions',
      type: 'actions-no-data',
      actions,
    },
  ]

  return (
    <Card isFullWidth>
      <HeadingRow headingLevel={3} headingText='Summary'>
        {!isViewMode && canEditWell(user) && (
          <ButtonsContainer containerStyle='right'>
            <Button label='Add new' onClick={() => navigate(`/wells/${wellId}/edit/aquifer-tests/new`)} />
          </ButtonsContainer>
        )}
      </HeadingRow>
      <SummaryTable
        fields={fields}
        setFields={setFields}
        columnHeaders={columnHeaders}
        defaultSortBy={{
          orderColumn: 'testDate',
        }}
        rowClickAction={rowClickAction}
        requestUrl={`wells/${wellId}/aquifer-test`}
        tableActionHandlers={{
          deleteAction: setDeletionRowId,
        }}
        confirmDeleteModalProps={{
          isShowModal: !!rowId,
          requestUrl: `wells/${wellId}/aquifer-test/${rowId}/edit`,
          heading: 'Delete aquifer test',
          bodyText: 'This will delete the aquifer test. Are you sure you wish to proceed?',
          successMessage: 'Aquifer test deleted',
          errorMessage: ERROR_MESSAGES.DELETE_FAILED,
          onModalClose,
        }}
        noResultsComponent={
          <NoAquiferTests
            handleNoTestsCheckbox={handleNoTestsCheckbox}
            isChecked={isNoTestsChecked}
            isViewMode={isViewMode || (!isViewMode && !canEditWell(user))}
          />
        }
        resultNameSingular='test'
        resultNamePlural='tests'
      />
    </Card>
  )
}
